import type { AcessoDef, AcessoLinha, ConviteAcessoLinha, ConviteLinha, OpcoesConcessao, Papel, SituacaoConvite, UsuarioLinha } from '@triade/contracts';
import { rotuloPapel } from '@/components/app-shell/papel';

/** Apresentacao e organizacao de dados da tela Usuarios e acessos. Nada de regra de permissao aqui: o teto e da API. */

export type AbaUsuarios = 'usuarios' | 'convites';

export interface BuscaUsuarios {
  usuario?: string;
  aba?: 'convites';
  q?: string;
}

/** `validateSearch`: descarta o que nao for valido em vez de quebrar a tela. */
export function lerBusca(bruto: Record<string, unknown>): BuscaUsuarios {
  const usuario = typeof bruto.usuario === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(bruto.usuario) ? bruto.usuario : undefined;
  const q = typeof bruto.q === 'string' && bruto.q.trim() ? bruto.q.trim().slice(0, 100) : undefined;
  return { ...(usuario ? { usuario } : {}), ...(bruto.aba === 'convites' ? { aba: 'convites' as const } : {}), ...(q ? { q } : {}) };
}

export const chaveAcesso = (a: { papel: Papel; empresaId?: string | null; grupoId?: string | null }): string => `${a.papel}|${a.empresaId ?? ''}|${a.grupoId ?? ''}`;

export const paraDef = (a: { papel: Papel; empresaId: string | null; grupoId: string | null }): AcessoDef => ({ papel: a.papel, empresaId: a.empresaId, grupoId: a.grupoId });

/** Ordem estavel por chave: o formulario compara arrays, entao a ordem nao pode depender de quem clicou primeiro. */
export const ordenarAcessos = (lista: readonly AcessoDef[]): AcessoDef[] => [...lista].sort((a, b) => chaveAcesso(a).localeCompare(chaveAcesso(b)));

/** "Vendedor · Demo Indústria", "Administrador (tenant inteiro)", "Consultor · grupo Demo". */
export function rotuloAcesso(a: Pick<AcessoLinha, 'papel' | 'empresaNome' | 'grupoNome' | 'perfilNome'> | ConviteAcessoLinha): string {
  // Perfil proprio da empresa (docs/PERMISSOES.md secao 11) no lugar do papel puro: "Vendedor · Gerente de margens · Demo Indústria".
  const papel = 'perfilNome' in a && a.perfilNome ? `${rotuloPapel(a.papel)} · ${a.perfilNome}` : rotuloPapel(a.papel);
  if (a.empresaNome) return `${papel} · ${a.empresaNome}`;
  if (a.grupoNome) return `${papel} · grupo ${a.grupoNome}`;
  return `${papel} (tenant inteiro)`;
}

/**
 * O que gravar para ir dos acessos de hoje ao desejado: `adicionar` (acessos novos) e `remover` (ids dos que sairam).
 * Acessos travados (o teto de quem edita nao deixa mexer) ficam de fora dos dois lados.
 */
export function diffAcessos(atuais: readonly AcessoLinha[], desejados: readonly AcessoDef[]): { adicionar: AcessoDef[]; remover: AcessoLinha[] } {
  const quer = new Set(desejados.map(chaveAcesso));
  const tem = new Set(atuais.map(chaveAcesso));
  return {
    adicionar: desejados.filter((d) => !tem.has(chaveAcesso(d))),
    remover: atuais.filter((a) => a.podeRemover && !quer.has(chaveAcesso(a))),
  };
}

/** Acessos que o editor mostra como escolhidos (os removiveis) e os travados (acima do teto de quem edita). */
export function separarAcessos(acessos: readonly AcessoLinha[]): { editaveis: AcessoDef[]; travados: AcessoLinha[] } {
  return { editaveis: ordenarAcessos(acessos.filter((a) => a.podeRemover).map(paraDef)), travados: acessos.filter((a) => !a.podeRemover) };
}

export const ROTULO_SITUACAO_CONVITE: Record<SituacaoConvite, string> = {
  PENDENTE: 'Pendente',
  ACEITO: 'Aceito',
  REVOGADO: 'Revogado',
  EXPIRADO: 'Vencido',
};

export type SituacaoUsuario = 'ATIVO' | 'INATIVO' | 'SENHA_TEMPORARIA';

export function situacaoUsuario(u: Pick<UsuarioLinha, 'ativo' | 'deveTrocarSenha'>): SituacaoUsuario {
  if (!u.ativo) return 'INATIVO';
  return u.deveTrocarSenha ? 'SENHA_TEMPORARIA' : 'ATIVO';
}

export const ROTULO_SITUACAO_USUARIO: Record<SituacaoUsuario, string> = {
  ATIVO: 'Ativo',
  INATIVO: 'Desativado',
  SENHA_TEMPORARIA: 'Aguardando 1º login',
};

const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Filtro de busca da tela (nome, e-mail ou papel): so apresentacao sobre a lista que a API devolveu. */
export function filtrarUsuarios(lista: readonly UsuarioLinha[], q: string | undefined): UsuarioLinha[] {
  const termo = semAcento(q ?? '').trim();
  if (!termo) return [...lista];
  return lista.filter((u) => semAcento(`${u.nome} ${u.email} ${u.acessos.map((a) => rotuloAcesso(a)).join(' ')}`).includes(termo));
}

export function filtrarConvites(lista: readonly ConviteLinha[], q: string | undefined): ConviteLinha[] {
  const termo = semAcento(q ?? '').trim();
  if (!termo) return [...lista];
  return lista.filter((c) => semAcento(`${c.nome ?? ''} ${c.email} ${c.acessos.map((a) => rotuloAcesso(a)).join(' ')}`).includes(termo));
}

/** O que sobrou para conceder? Sem opcao nenhuma, quem consulta nao pode convidar (a API tambem nega). */
export function podeConceder(opcoes: OpcoesConcessao | undefined): boolean {
  return !!opcoes && (opcoes.tenant.length > 0 || opcoes.grupos.length > 0 || opcoes.empresas.length > 0);
}
